import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components';
import type { ScenarioCredential } from './types';
import { TextField } from './ui';
import type { ApiOutcome } from '../../hooks/useScenario';

interface Props {
  credentials: ScenarioCredential[];
  error: string | null;
  onSave: (credential: Omit<ScenarioCredential, 'id'> & { id?: string }) => Promise<ApiOutcome<ScenarioCredential>>;
  onRemove: (id: string) => Promise<boolean>;
}

const EMPTY = { ref: '', header_name: 'Authorization', header_value: '' };

export const CredentialsManager: React.FC<Props> = ({ credentials, error, onSave, onRemove }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Omit<ScenarioCredential, 'id'> & { id?: string }>(EMPTY);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    const outcome = await onSave(draft);
    setSaving(false);
    if (outcome.ok) {
      setDraft(EMPTY);
      setMessage(null);
    } else {
      setMessage(outcome.message);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.credentialsHint')}</p>
      {(error || message) && <p className="text-xs text-red-600 dark:text-red-400">{message || error}</p>}
      {credentials.map(credential => (
        <div key={credential.id} className="flex items-center justify-between gap-3 text-sm">
          <span className="font-mono">{credential.ref}</span>
          <span className="text-gray-500 dark:text-gray-400">{credential.header_name}: ••••••</span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDraft(credential)}
              aria-label={t('agents.scenario.edit')}
            >
              <span className="material-icons text-base">edit</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('agents.scenario.remove')}
              onClick={() => {
                if (window.confirm(t('agents.scenario.confirmRemoveCredential', { ref: credential.ref }))) {
                  onRemove(credential.id);
                }
              }}
            >
              <span className="material-icons text-base">delete</span>
            </Button>
          </div>
        </div>
      ))}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_2fr_auto]">
        <TextField
          label={t('agents.scenario.credentialRef')}
          value={draft.ref}
          onChange={value => setDraft({ ...draft, ref: value })}
          issues={[]}
          pointer="/credential/ref"
          placeholder="tgp-api-token"
          monospace
        />
        <TextField
          label={t('agents.scenario.headerName')}
          value={draft.header_name}
          onChange={value => setDraft({ ...draft, header_name: value })}
          issues={[]}
          pointer="/credential/header_name"
        />
        <TextField
          label={t('agents.scenario.headerValue')}
          value={draft.header_value}
          onChange={value => setDraft({ ...draft, header_value: value })}
          issues={[]}
          pointer="/credential/header_value"
          placeholder="Bearer …"
        />
        <div className="flex items-end gap-1 pb-0.5">
          <Button size="sm" onClick={submit} disabled={saving || !draft.ref || !draft.header_value}>
            {draft.id ? t('common.save') : t('agents.scenario.addCredential')}
          </Button>
          {draft.id && (
            <Button variant="ghost" size="sm" onClick={() => setDraft(EMPTY)}>
              {t('common.cancel')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
